/**
 * Maps internal payload types (used by AuthContext / screens) to the
 * exact API request shapes, and API responses to internal models.
 */
import { getDeviceTypeCode } from '../../platform/device';
import type { LoginPayload, SignUpPayload } from '../../types/auth';
import type {
  LoginRequest,
  RefreshTokenRequest,
  RevokeTokenRequest,
} from './authTypes';

/**
 * Maps the loose LoginPayload used by screens/context to the strict API request.
 * The `email` field in LoginPayload holds whatever the user typed in the
 * identifier field (email OR username) so we forward it directly to
 * `userNameOrEmail`.
 */
export const mapLoginPayload = (
  payload: LoginPayload,
  deviceId: string,
  deviceToken?: string,
): LoginRequest => ({
  userNameOrEmail: (payload.email ?? payload.identifier ?? '').trim(),
  password: payload.password ?? '',
  deviceId,
  deviceType: getDeviceTypeCode(),
  deviceToken,
});

/**
 * Constructs the RefreshToken request body.
 */
export const mapRefreshTokenRequest = (
  refreshToken: string,
  deviceId: string,
  deviceType: string,
): RefreshTokenRequest => ({ refreshToken, deviceId, deviceType });

/**
 * Constructs the RevokeToken (logout) request body.
 */
export const mapRevokeTokenRequest = (
  refreshToken: string,
  deviceId: string,
  deviceType: string,
): RevokeTokenRequest => ({ refreshToken, deviceId, deviceType });

/**
 * Maps the loose SignUpPayload (from setupProfile screen) to the
 * OnboardingCreateAccount API request.
 */
export const mapSignUpToCreateAccount = (
  payload: SignUpPayload,
  deviceId: string,
  deviceToken?: string,
): {
  email: string;
  firstName: string;
  password: string;
  userName: string;
  middleName?: string;
  lastName?: string;
  signUpMethod: 'Email';
  referralCode?: string;
  deviceId: string;
  deviceType: string;
  deviceToken?: string;
} => {
  
  let deviceType: string = getDeviceTypeCode();

  return {
    email: payload.email.trim(),
    firstName: (payload.firstName ?? '').trim(),
    password: payload.password ?? '',
    userName: (payload.username?.trim() || payload.email.split('@')[0]).trim(),
    ...(payload.lastName?.trim() ? { lastName: payload.lastName.trim() } : {}),
    signUpMethod: 'Email',
    ...(payload.referralCode?.trim()
      ? { referralCode: payload.referralCode.trim() }
      : {}),
    deviceId,
    deviceType,
    deviceToken,
  };
};
